<template>
    <BaseLink
        :href="data.return_url"
        class="group relative flex items-start gap-3 md:gap-4 px-4 py-3 md:px-5 md:py-(--spacing-history-row-y-md) text-left transition-[background-color,border-color] w-full rounded-lgr hover:bg-surface-hover hover:border hover:border-history-row-hover-border active:bg-history-row-active-bg active:border active:border-history-row-hover-border border border-transparent"
        :data="{ chat_source: CHATSOURCE.PAST_CONVERSATIONS }">
        <!-- Main content -->
        <div class="flex-1 min-w-0">
            <!-- Date above summary -->
            <p class="scaffold-body-small text-history-date mb-1">
                <BaseDate :value="data.updated_at" :format="dateFormat" /><span v-if="data.message_count">{{
                    " " + t("components.pastConversation.messageCount", { count: data.message_count }, data.message_count)
                }}</span>
            </p>

            <!-- Title -->
            <p v-if="!isRenaming" class="nadia-chat-semibold text-history-title fs-mask truncate">
                {{ truncate(data.title, { length: 120, omission: "..." }) }}
            </p>
            <div v-else class="flex w-full gap-2 items-center" @click.prevent.stop @keyup.prevent.stop>
                <input
                    ref="renameInput"
                    v-model.trim="newTitle"
                    :aria-label="t('components.pastConversation.title')"
                    class="grow body-small focus-field rounded-lg p-2 w-full border border-control-border fs-mask"
                    :maxlength="120"
                    @keydown.enter.stop="handleTitleEnter"
                    @keydown.escape.stop="cancelRename" />
                <BaseButton theme="ghost" size="sm" icon-only :aria-label="t('components.pastConversation.saveTitle')" :disabled="!newTitle" @click="handleSaveTitle">
                    <i class="bi bi-check" aria-hidden="true" />
                </BaseButton>
                <BaseButton theme="ghost" size="sm" icon-only :aria-label="t('components.pastConversation.cancelRename')" @click="isRenaming = false">
                    <i class="bi bi-x" aria-hidden="true" />
                </BaseButton>
            </div>
        </div>

        <!-- Actions column -->
        <div class="shrink-0 flex items-center gap-1 self-center" @click.stop.prevent>
            <BaseDropdownMenu :sections="menuSections" side="left" align="center" :side-offset="10" @select="handleMenuSelect" @close-auto-focus="handleMenuCloseAutoFocus">
                <template #trigger>
                    <BaseButton theme="ghost" size="sm" icon-only type="button" :aria-label="t('components.pastConversation.options')" class="hist-kebab" @click.stop.prevent>
                        <i class="bi bi-three-dots" aria-hidden="true" />
                    </BaseButton>
                </template>
            </BaseDropdownMenu>
        </div>
    </BaseLink>
</template>

<script setup lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { CHATSOURCE } from "~vue/utils/constants";
import { type DateFormat } from "~vue/utils/formatDate";
import { unlessComposing } from "~vue/utils/keyboard";
import truncate from "lodash/truncate";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

export interface ConversationData {
    kind: "v1" | "v2";
    hashid: string;
    uuid: string;
    title: string;
    return_url: string;
    updated_at: string;
    message_count: number;
    preview: string | null;
}

interface Props {
    deletionDisabled?: boolean;
    data: ConversationData;
}

const { data, deletionDisabled = false } = defineProps<Props>();

const emit = defineEmits<{
    delete: [payload: { kind: "v1" | "v2"; hashid: string; uuid: string }];
    rename: [payload: { kind: "v1" | "v2"; hashid: string; uuid: string; newTitle: string }];
}>();

const dateFormat = computed<DateFormat>(() => {
    const dt = convertToDateTime(data.updated_at);
    const now = convertToDateTime(new Date().toISOString());
    const diffDays = now.startOf("day").diff(dt.startOf("day"), "days").days;

    return diffDays > 300 ? "date" : "month-day";
});

const menuSections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [{ id: "rename", type: "button", label: t("components.pastConversation.rename"), icon: "bi bi-pencil" }];
    // Delete is hidden when the user is on legal/retention hold; rename stays
    // available because the v1 server endpoint only blocks delete.
    if (!deletionDisabled) {
        items.push({ id: "delete", type: "button", label: t("common.delete"), icon: "bi bi-trash" });
    }
    return [{ id: "actions", items }];
});

const handleDeletePastConversation = () => {
    emit("delete", { kind: data.kind, hashid: data.hashid, uuid: data.uuid });
};

const renameInput = ref<HTMLInputElement>();
const isRenaming = ref(false);
const newTitle = ref("");

const handleStartRename = () => {
    newTitle.value = data.title ?? "";
    isRenaming.value = true;
};

const handleMenuCloseAutoFocus = async (event: Event) => {
    if (!isRenaming.value) {
        return;
    }

    event.preventDefault();
    await nextTick();
    // Reka removes its modal focus scope in a timer after this event.
    globalThis.setTimeout(() => {
        renameInput.value?.focus();
        renameInput.value?.select();
    }, 0);
};

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "rename") {
        handleStartRename();
    } else if (item.id === "delete") {
        handleDeletePastConversation();
    }
};

const handleSaveTitle = () => {
    if (!newTitle.value) {
        return;
    }
    isRenaming.value = false;
    emit("rename", { kind: data.kind, hashid: data.hashid, uuid: data.uuid, newTitle: newTitle.value });
};

const handleTitleEnter = unlessComposing(handleSaveTitle);
const cancelRename = unlessComposing(() => (isRenaming.value = false));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

/* The spec's kebab is hidden until the row is hovered, but only for pointers
   that can hover: touch users keep it always visible, since focus-visible
   only rescues keyboards. Flag off it stays always visible as today. */
@media (hover: hover) {
    html[data-theme="tonal"] .hist-kebab {
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    html[data-theme="tonal"] .group:hover .hist-kebab,
    html[data-theme="tonal"] .hist-kebab:focus-visible,
    html[data-theme="tonal"] .hist-kebab[aria-expanded="true"] {
        opacity: 1;
    }
}
</style>
